const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "/api/v1";

const APP_BASE_URL = process.env.NEXT_PUBLIC_APP_URL ?? "";

export function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export type ApiValidationErrors = Record<string, string[]>;

export class ApiError extends Error {
  status: number;
  errors?: ApiValidationErrors;

  constructor(
    message: string,
    status: number,
    errors?: ApiValidationErrors,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type");
  const isJson = contentType?.includes("application/json");

  const payload = isJson
    ? await response.json()
    : { message: undefined };

  if (!response.ok) {
    const fallbackMessage = response.status === 413
      ? "File size exceeds the 5 MB limit."
      : "The request could not be completed.";

    throw new ApiError(
      payload.message ?? fallbackMessage,
      response.status,
      payload.errors,
    );
  }

  return payload as T;
}

export async function initializeCsrf(): Promise<void> {
  const response = await fetch(`${APP_BASE_URL}/sanctum/csrf-cookie`, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
    cache: "no-store",
  });

  if (!response.ok && response.status !== 204) {
    throw new ApiError(
      "Unable to initialize the secure session.",
      response.status,
    );
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const isFormData = options.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
  });

  return parseResponse<T>(response);
}
